import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import type { Trip } from './trips'

const api = clientFor('/dsp/api')

/**
 * 调度任务状态机（services/dsp/internal/dispatch/model.go）：
 * pending → assigned → doing → done；pending / assigned → cancelled。
 */
export type TaskStatus = 'pending' | 'assigned' | 'doing' | 'done' | 'cancelled'

/** 任务来源：跨库只存单号字符串，不建外键 */
export type TaskSourceType = 'ops_order' | 'wms_outbound' | 'manual'

/** 优先级：智能派车时 urgent 先走 */
export type TaskPriority = 'normal' | 'urgent'

/** 可在页面上触发的任务动作，与后端 POST /dispatch/tasks/:id/<action> 一一对应 */
export type TaskAction = 'assign' | 'cancel'

/** 待调度任务。字段与 services/dsp/internal/dispatch/model.go 的 Task 一一对应。 */
export interface Task {
  id: number
  tenant_id: number
  /** 任务号，前缀 DT */
  task_no: string
  source_type: TaskSourceType
  /** 外部单号：ops 订单号 / wms 出库单号 */
  source_no: string
  cargo_desc: string
  weight_kg: number
  volume_cm3: number
  pickup_addr: string
  deliver_addr: string
  priority: TaskPriority
  status: TaskStatus
  trip_id: number
  created_by: string
  created_at: string
  updated_at: string
}

/** 列表行：任务 + 所挂行车任务单号与车牌号 */
export interface TaskRow extends Task {
  trip_doc_no: string
  plate_no: string
}

/** 手工建任务的入参：source_type=manual 时外部单号可留空，引用外部单号时必填 */
export interface TaskInput {
  source_type: TaskSourceType
  source_no?: string
  cargo_desc: string
  weight_kg: number
  volume_cm3: number
  pickup_addr?: string
  deliver_addr?: string
  priority: TaskPriority
}

export interface TaskQuery extends PageQuery {
  keyword?: string
  status?: string
  priority?: string
  source_type?: string
}

/** 一键智能派车的返回：派上的、没派上的（留在 pending）、用到的行车任务 */
export interface AutoResult {
  assigned: TaskRow[]
  unassigned: TaskRow[]
  trips: Trip[]
}

/** 与后端 services/dsp/internal/dispatch/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: '待调度',
  assigned: '已派车',
  doing: '运输中',
  done: '已完成',
  cancelled: '已取消',
}

/** 与后端 services/dsp/internal/dispatch/model.go 的 SourceNames 同源两份 */
export const SOURCE_LABELS: Record<TaskSourceType, string> = {
  ops_order: '运营订单',
  wms_outbound: '出库单',
  manual: '手工',
}

/** 与后端 services/dsp/internal/dispatch/model.go 的 PriorityNames 同源两份 */
export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  normal: '普通',
  urgent: '加急',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<TaskStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  assigned: 'info',
  doing: 'primary',
  done: 'success',
  cancelled: 'danger',
}

/** 各状态下允许的动作，抄后端 internal/dispatch/service.go：只有 pending 能派车，pending/assigned 能取消 */
const ACTIONS: Record<TaskStatus, TaskAction[]> = {
  pending: ['assign', 'cancel'],
  assigned: ['cancel'],
  doing: [],
  done: [],
  cancelled: [],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: TaskStatus, action: TaskAction): boolean {
  return ACTIONS[status].includes(action)
}

export const listTasks = (q: TaskQuery): Promise<PageResult<TaskRow>> =>
  api.get<PageResult<TaskRow>>('/dispatch/tasks', { ...q })

export const getTask = (id: number): Promise<TaskRow> => api.get<TaskRow>(`/dispatch/tasks/${id}`)

export const createTask = (input: TaskInput): Promise<Task> => api.post<Task>('/dispatch/tasks', input)

/** 手工派车：body 形如 {trip_id}，容量超限或状态不符后端回 409 */
export const assignTask = (id: number, tripId: number): Promise<TaskRow> =>
  api.post<TaskRow>(`/dispatch/tasks/${id}/assign`, { trip_id: tripId })

export const cancelTask = (id: number): Promise<void> => api.post<void>(`/dispatch/tasks/${id}/cancel`, {})

/** 一键智能派车：pending 任务装进空闲且装得下的车，返回派上/未派上明细与用到的行车任务 */
export const autoDispatch = (): Promise<AutoResult> => api.post<AutoResult>('/dispatch/auto', {})
